import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, setUnauthorizedHandler } from './api';
import type { Me, Permission, User } from './types';
import { autoClockIn, autoClockOut } from '../pages/timeclock/autoClock';

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** Prüft ein Recht des angemeldeten Benutzers. */
  can: (p: Permission) => boolean;
  canAny: (...p: Permission[]) => boolean;
  refresh: () => Promise<void>;
  /** Ergebnis: Meldung zum automatischen „Kommen“ am Ladengerät (pages/timeclock/autoClock.ts) oder null */
  login: (username: string, password: string, totp?: string) => Promise<{ text: string; tone: 'success' | 'info' } | null>;
  logout: () => Promise<void>;
  /** Öffentliche Einstellung lesen, z. B. setting<string>('shop.name') */
  setting: <T = unknown>(key: string) => T;
}

const Ctx = createContext<AuthState | null>(null);

/**
 * Letzter bekannter Anmeldestatus für den Offline-Betrieb: Fällt das Internet aus und die Kasse wird neu geladen,
 * bleibt der Benutzer angemeldet (der Server prüft die Sitzung beim nächsten Online-Zugriff wieder).
 */
const ME_CACHE_KEY = 'kk_me';
function readCachedMe(): Me | null {
  try {
    const raw = localStorage.getItem(ME_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}
function writeCachedMe(me: Me | null) {
  try {
    if (me) localStorage.setItem(ME_CACHE_KEY, JSON.stringify(me));
    else localStorage.removeItem(ME_CACHE_KEY);
  } catch { /* Speicher nicht verfügbar */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const fresh = await api.get<Me>('/auth/me');
      writeCachedMe(fresh);
      setMe(fresh);
    } catch (e) {
      // Ohne Netz: zuletzt bekannten Stand weiterverwenden, sonst abmelden
      if (e instanceof ApiError && e.status === 0) setMe(readCachedMe());
      else {
        writeCachedMe(null);
        setMe(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    setUnauthorizedHandler((code) => {
      if (code === 'PASSWORD_CHANGE_REQUIRED' || code === 'TWO_FACTOR_SETUP_REQUIRED') void refresh();
      else {
        writeCachedMe(null);
        setMe(null);
      }
    });
  }, [refresh]);

  const login = useCallback(async (username: string, password: string, totp?: string) => {
    const r = await api.post<{ user: User }>('/auth/login', { username, password, totp: totp || undefined });
    // Am Ladengerät: „Kommen“ gleich mitstempeln (vor dem Laden der Oberfläche, damit sie den Stand schon zeigt)
    const clock = await autoClockIn(r.user);
    qc.clear();
    await refresh();
    return clock;
  }, [qc, refresh]);

  const logout = useCallback(async () => {
    // Am Ladengerät: „Gehen“ stempeln, solange die Sitzung noch gilt
    if (me) await autoClockOut(me.user);
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    qc.clear();
    writeCachedMe(null);
    setMe(null);
  }, [qc, me]);

  const value = useMemo<AuthState>(() => {
    const perms = new Set(me?.user.permissions ?? []);
    return {
      me,
      loading,
      can: (p) => perms.has(p),
      canAny: (...ps) => ps.some((p) => perms.has(p)),
      refresh,
      login,
      logout,
      setting: <T,>(key: string) => me?.settings[key] as T,
    };
  }, [me, loading, refresh, login, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth außerhalb von AuthProvider');
  return c;
}
